// src/components/error-boundary/ErrorBoundary.tsx
import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "lucide-react";
import { ErrorReportForm } from "./ErrorReportForm";
import { reportBoundaryError } from "./reportBoundaryError";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isDetailsExpanded: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isDetailsExpanded: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isDetailsExpanded: false,
    };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    reportBoundaryError(error, info);
  }

  private toggleDetails = () => {
    this.setState((state) => ({
      isDetailsExpanded: !state.isDetailsExpanded,
    }));
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-2xl w-full">
            <AlertTriangleIcon className="w-12 h-12 text-destructive mx-auto" />
            <h1 className="text-2xl font-bold">Algo salió mal</h1>

            <div className="text-muted-foreground">
              <p className="mb-2">
                {this.state.error?.message || "Ha ocurrido un error inesperado"}
              </p>
              <p className="text-sm">
                Ubicación actual: <code>{window.location.pathname}</code>
              </p>
            </div>

            <Button
              onClick={this.toggleDetails}
              variant="outline"
              className="flex items-center gap-2 mx-auto"
            >
              {this.state.isDetailsExpanded ? (
                <>
                  Ocultar detalles <ChevronUpIcon className="w-4 h-4" />
                </>
              ) : (
                <>
                  Ver detalles técnicos <ChevronDownIcon className="w-4 h-4" />
                </>
              )}
            </Button>

            {this.state.isDetailsExpanded && (
              <div className="text-left bg-muted p-4 rounded-lg mt-4">
                <h3 className="font-medium mb-2">Detalles del error:</h3>
                <div className="font-mono text-xs overflow-auto max-h-[200px] whitespace-pre-wrap">
                  {this.state.error?.stack || "No hay stack trace disponible"}
                </div>
                <p className="text-xs mt-4 text-muted-foreground">
                  Componente: {this.state.error?.name || "Desconocido"}
                </p>
              </div>
            )}

            <ErrorReportForm
              errorMessage={this.state.error?.message}
              errorStack={this.state.error?.stack}
              errorName={this.state.error?.name}
              pathname={window.location.pathname}
            />

            <Button
              onClick={() => {
                window.location.href = "/";
              }}
              variant="secondary"
              className="mt-4"
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
